import { useNavigate } from "react-router-dom";
import { useSetLocale } from "../i18n/useLocale";
import { FormattedMessage } from "react-intl";

/**
 * First-launch locale picker (D-44, I18N-9.2).
 * The picker page stays dir="ltr" regardless of what the app locale becomes
 * — the EN button is on the inline-start, AR on inline-end in EN reading order.
 * Only after the user picks does the rest of the app flip (via IntlBoot useEffect).
 */
export function LocalePicker() {
  const setLocale = useSetLocale();
  const navigate = useNavigate();

  const choose = (locale: "en" | "ar") => {
    setLocale(locale);
    navigate("/login", { replace: true });
  };

  return (
    <main
      dir="ltr"
      data-testid="locale-picker"
      className="min-h-screen relative bg-primary"
    >
      {/* Brand chrome shared with Login (2026-05-17 FIND-C). dir="ltr" is
          preserved on the wrapper because the picker MUST present both
          locales in EN reading order until the user picks. */}
      <div
        className="absolute inset-0 bg-[url('/brand/watercolor-master.jpg')] bg-cover bg-center"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-primary/55" aria-hidden="true" />

      <div className="relative min-h-screen flex flex-col items-center pbs-6">
        <div className="flex flex-col items-center mbe-4">
          <img
            src="/brand/unayzah-municipality-logo.png"
            alt=""
            aria-hidden="true"
            className="w-24 sm:w-28 h-auto mbe-1"
            data-testid="picker-main-logo"
          />
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="picker-org-parent"
          >
            <FormattedMessage id="app_org_parent_dept" />
          </p>
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="picker-org-sub"
          >
            <FormattedMessage id="app_org_sub_dept" />
          </p>
        </div>

        <div className="flex-1 flex flex-col justify-center pbe-8">
          <h2
            className="text-base font-semibold text-white text-center mbe-3"
            data-testid="picker-app-name-full"
          >
            <FormattedMessage id="app_name_full" />
          </h2>

          <div className="bg-surface-raised rounded-lg shadow-sm border border-divider p-8 text-center">
            <h1 className="text-2xl font-semibold text-on-surface mbe-8">
              <FormattedMessage id="LABEL_PICK_LANGUAGE" defaultMessage="Choose your language" />
            </h1>
            <div className="flex gap-8 justify-center">
              <button
                type="button"
                onClick={() => choose("en")}
                data-testid="pick-en"
                className="w-48 h-16 bg-surface-raised border-2 border-divider rounded-lg text-xl font-semibold hover:border-primary hover:bg-secondary-container focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-200 focus-visible:outline-none"
              >
                English
              </button>
              <button
                type="button"
                onClick={() => choose("ar")}
                data-testid="pick-ar"
                className="w-48 h-16 bg-surface-raised border-2 border-divider rounded-lg text-xl font-semibold hover:border-primary hover:bg-secondary-container focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-200 focus-visible:outline-none"
              >
                العربية
              </button>
            </div>
          </div>

          <p
            className="text-xs text-white/70 text-center mbs-4"
            data-testid="picker-app-publisher"
          >
            <FormattedMessage id="app_publisher" />
          </p>
        </div>
      </div>
    </main>
  );
}
